import { computed, MaybeRef, unref } from 'vue'
import { RateCardComparisonResult } from '@/gql/graphql'
import { $t } from '@/composables/translation'
import { useRateCardBandRangeText } from '@/components/Job/composables/rateCardBandRangeText'
import { RateCardBand } from '@/components/Job/composables/rateCardGuidance'

export type { RateCardBand }

export type RateCardWarning = {
  title: string
  description: string
  // Which edge fired — not shown as copy, only exposed so tests can pin the edge without
  // asserting on rendered text.
  comparison: RateCardComparisonResult.AboveMaximum | RateCardComparisonResult.BelowMinimum
}

// Direct-hire form only — both edges warn here (above the maximum and below the minimum),
// unlike the supplier-leg comparator consumers, which only warn above the maximum.
export function useHireRateCardBandGuidance(options: {
  band: MaybeRef<RateCardBand>
  rate: MaybeRef<string | number>
  currencySymbol: MaybeRef<string>
}) {
  const band = computed(() => unref(options.band))
  const comparison = computed(() => band.value?.comparison ?? null)

  // A job's rate columns store 0 for "nothing entered," never a real rate — don't warn on it.
  const rateIsUnset = computed(() => Number(unref(options.rate)) === 0)

  const bandRangeText = useRateCardBandRangeText(band, options.currencySymbol)

  const warning = computed((): RateCardWarning | undefined => {
    if (rateIsUnset.value) return undefined

    if (comparison.value === RateCardComparisonResult.AboveMaximum) {
      return {
        title: $t('Outside the rate card range'),
        description: $t(':range — above the maximum', { range: bandRangeText.value ?? '' }),
        comparison: RateCardComparisonResult.AboveMaximum,
      }
    }

    if (comparison.value === RateCardComparisonResult.BelowMinimum) {
      return {
        title: $t('Outside the rate card range'),
        description: $t(':range — below the minimum', { range: bandRangeText.value ?? '' }),
        comparison: RateCardComparisonResult.BelowMinimum,
      }
    }

    return undefined
  })

  return { warning }
}
